import { Deduction } from "@/core/entities/deduction/deduction";
import { ApprovalRemittance } from "@/core/entities/deduction";
import { BrandMember } from "@/core/entities/lookup/brandMember/BrandMember";
import { IDeductionColumns } from "@/core/repositories/deduction/IDeduction.repository";
import { AppConfig } from "@/infrastructure/services/config";
import { FilterDateRange } from "@/presentation/deduction/components/DeductionFilter/interfaces";
import { SelectOption, TableSortInfoColumnType } from "@ir/ui";
import { atom } from "jotai";

const _AppConfig = AppConfig.getInstance();
const PAGINATION_PAGE_LIMIT = _AppConfig.PAGINATION_PAGE_LIMIT;

type Option = SelectOption;

export type ApprovalAction = "APPROVE" | "REJECT" | null;

export interface ApprovalFilterInfo {
  lastFilteredAt: Date | null; 
  search: string | null;
  checkFromDate: string | null;
  checkToDate: string | null;
  checkDateRange: FilterDateRange | null;
  invoiceFromDate: string | null;
  invoiceToDate: string | null;
  invoiceDateRange: FilterDateRange | null;
  purchaser: Option[];
  dc: Option[];
  glCode: Option[];
  analyst: Option[];
  approver: Option[];
  sortColumn: string | null;
  sortOrder: TableSortInfoColumnType["order"] | null;
}

export interface ApprovalState {
  groupByCheck: boolean;
  approvalRemittances: ApprovalRemittance[];
  expandedRemittanceKey: number | null;
  loggedInBrandMember: BrandMember | null;
  selectedApprovals: Deduction[];
  selectedApproval: Deduction | null;
  isFilterApplied: boolean;
  isApprovalSending: boolean;
  userHasClearedApprovalFilters: boolean;
  filterInfo: ApprovalFilterInfo;
  approvals: Deduction[];
  columns: IDeductionColumns[];
  isLoading: boolean;
  isRefreshing: boolean;
  isApprovalModalOpen: boolean;
  approvalAction: ApprovalAction;
  error: Error | null;
  totalCount: number;
  currentPage: number;
  pageLimit: number;
}

const initialState: ApprovalState = {
  groupByCheck: true,
  approvalRemittances: [],
  expandedRemittanceKey: null,
  loggedInBrandMember: null,
  selectedApprovals: [],
  selectedApproval: null,
  isFilterApplied: false,
  isApprovalSending: false,
  userHasClearedApprovalFilters: false,
  approvals: [],
  columns: [],
  isLoading: false,
  isRefreshing: false,
  isApprovalModalOpen: false,
  approvalAction: null,
  error: null,
  totalCount: 0,
  currentPage: 1,
  pageLimit: PAGINATION_PAGE_LIMIT,
  filterInfo: {
    lastFilteredAt: null,
    search: null,
    checkFromDate: null,
    checkToDate: null,
    checkDateRange: null,
    invoiceFromDate: null,
    invoiceToDate: null,
    invoiceDateRange: null,
    purchaser: [],
    dc: [],
    glCode: [],
    analyst: [],
    approver: [],
    sortColumn: null,
    sortOrder: null,
  },
};

// Base atoms
export const approvalAtom = atom<ApprovalState>(initialState);

export const loggedInBrandMemberAtom = atom(get => get(approvalAtom).loggedInBrandMember);
export const groupByCheckAtom = atom(get => get(approvalAtom).groupByCheck);
export const approvalRemittancesAtom = atom(get => get(approvalAtom).approvalRemittances);
export const expandedRemittanceKeyAtom = atom(get => get(approvalAtom).expandedRemittanceKey);

// Derived atoms for specific states
export const filterInfoAtom = atom(get => get(approvalAtom).filterInfo);

export const isFilterAppliedAtom = atom(get => get(approvalAtom).isFilterApplied);

export const userHasClearedApprovalFiltersAtom = atom(get => get(approvalAtom).userHasClearedApprovalFilters);

export const approvalsAtom = atom(get => get(approvalAtom).approvals);
export const selectedApprovalsAtom = atom(get => get(approvalAtom).selectedApprovals);
export const selectedApprovalAtom = atom(get => get(approvalAtom).selectedApproval);
export const columnsAtom = atom(get => get(approvalAtom).columns);
export const isLoadingAtom = atom(get => get(approvalAtom).isLoading);
export const isRefreshingAtom = atom(get => get(approvalAtom).isRefreshing);
export const isApprovalModalOpenAtom = atom(get => get(approvalAtom).isApprovalModalOpen);
export const approvalActionAtom = atom(get => get(approvalAtom).approvalAction);
export const errorAtom = atom(get => get(approvalAtom).error);
export const totalCountAtom = atom(get => get(approvalAtom).totalCount);
export const currentPageAtom = atom(get => get(approvalAtom).currentPage);
export const pageLimitAtom = atom(get => get(approvalAtom).pageLimit);
